/**
 * Resume-run fixtures (dev only)
 *
 * Each fixture models a conversation page reloading MID-RUN:
 *   - `seedSteps`  — what `GET /runs/{runId}` returns at reload time (the steps
 *     persisted so far). The adapter resume branch seeds the reducer from these
 *     via `stepsToIterations`, so the trace that already happened is on screen
 *     immediately.
 *   - `liveEvents` — the `agent.run.*` events that arrive AFTER reload over the
 *     WS, streamed on top of the seed until a terminal event.
 *   - `status`     — the run status the snapshot reports. `completed` models the
 *     race where the run finished between page load and the `runs.get` fetch
 *     (no live events; the seeded steps already include the final answer).
 *
 * The shapes match the real backend contract (`RunStepResponse`,
 * `PlatformEvent`) so the page drives the exact seed + reconcile code path the
 * production adapter uses.
 */

import type { PlatformEvent } from '@/types/events';
import type { RunStepResponse } from '@/types';

export interface ResumeFixture {
  id: string;
  title: string;
  description: string;
  runId: string;
  status: 'running' | 'completed';
  seedSteps: RunStepResponse[];
  liveEvents: PlatformEvent[];
}

let evtSeq = 0;
function ev(eventType: string, runId: string, data: Record<string, unknown>): PlatformEvent {
  evtSeq += 1;
  return {
    eventId: `resume-evt-${evtSeq}`,
    eventType,
    source: 'dev-resume',
    subjectId: runId,
    data: { ...data, runId },
    metadata: { timestamp: `2026-01-01T00:00:${String(evtSeq).padStart(2, '0')}Z` },
    orgId: 'org-dev',
  };
}

const RUN_MID_TOOL = 'rdr-resume-tool';
const RUN_MID_REASON = 'rdr-resume-reason';
const RUN_RACE = 'rdr-resume-race';

export const RESUME_FIXTURES: ResumeFixture[] = [
  {
    id: 'mid-tool-call',
    title: 'Reload after first tool call',
    description:
      'Pre-reload: one reasoning pass + a completed search tool call (seeded). ' +
      'Post-reload the agent reads a URL, then streams its final answer.',
    runId: RUN_MID_TOOL,
    status: 'running',
    seedSteps: [
      {
        type: 'reasoning',
        iteration: 0,
        timestamp: '2026-01-01T00:00:00Z',
        text: 'The user wants today’s weather. Let me search for it.',
      },
      {
        type: 'tool_call',
        iteration: 0,
        timestamp: '2026-01-01T00:00:01Z',
        toolName: 'web_search',
        toolDisplayName: 'Web Search',
        toolCallId: 'tc-seed-search',
        input: { query: 'weather today San Francisco' },
        output: 'San Francisco: Sunny, 72°F, light breeze.',
        label: 'Searching the web',
      },
    ],
    liveEvents: [
      ev('agent.run.tool_call', RUN_MID_TOOL, {
        toolName: 'read_url',
        toolDisplayName: 'Read URL',
        toolCallId: 'tc-live-read',
        input: { url: 'https://weather.example.com/sf' },
        iteration: 1,
        label: 'Reading the forecast',
      }),
      ev('agent.run.tool_result', RUN_MID_TOOL, {
        toolName: 'read_url',
        toolCallId: 'tc-live-read',
        success: true,
        output: 'Hourly: clear through the evening, high of 73°F.',
        iteration: 1,
      }),
      ev('agent.run.final_answer_start', RUN_MID_TOOL, { iteration: 1 }),
      ev('agent.run.final_answer_delta', RUN_MID_TOOL, {
        iteration: 1,
        delta: 'It’s sunny in San Francisco today — around 72°F, ',
        accumulated: 'It’s sunny in San Francisco today — around 72°F, ',
      }),
      ev('agent.run.final_answer_delta', RUN_MID_TOOL, {
        iteration: 1,
        delta: 'clear through the evening with a high near 73°F.',
        accumulated:
          'It’s sunny in San Francisco today — around 72°F, clear through the evening with a high near 73°F.',
      }),
      ev('agent.run.final_answer_stop', RUN_MID_TOOL, {
        iteration: 1,
        content:
          'It’s sunny in San Francisco today — around 72°F, clear through the evening with a high near 73°F.',
      }),
      ev('agent.run.completed', RUN_MID_TOOL, {
        response:
          'It’s sunny in San Francisco today — around 72°F, clear through the evening with a high near 73°F.',
        steps: [],
        durationMs: 8200,
      }),
    ],
  },
  {
    id: 'mid-reasoning',
    title: 'Reload mid-reasoning (no tools yet)',
    description:
      'Pre-reload: only partial reasoning is persisted (seeded). Post-reload the ' +
      'reasoning continues and the agent answers directly with no tools.',
    runId: RUN_MID_REASON,
    status: 'running',
    seedSteps: [
      {
        type: 'reasoning',
        iteration: 0,
        timestamp: '2026-01-01T00:00:00Z',
        text: 'This is a definitional question, so I can answer from knowledge.',
      },
    ],
    liveEvents: [
      ev('agent.run.reasoning_delta', RUN_MID_REASON, {
        iteration: 0,
        delta: ' Let me phrase it concisely.',
        accumulated:
          'This is a definitional question, so I can answer from knowledge. Let me phrase it concisely.',
      }),
      ev('agent.run.reasoning_stop', RUN_MID_REASON, { iteration: 0 }),
      ev('agent.run.final_answer_start', RUN_MID_REASON, { iteration: 0 }),
      ev('agent.run.final_answer_delta', RUN_MID_REASON, {
        iteration: 0,
        delta: 'A closure is a function bundled with references to its surrounding state.',
        accumulated: 'A closure is a function bundled with references to its surrounding state.',
      }),
      ev('agent.run.final_answer_stop', RUN_MID_REASON, {
        iteration: 0,
        content: 'A closure is a function bundled with references to its surrounding state.',
      }),
      ev('agent.run.completed', RUN_MID_REASON, {
        response: 'A closure is a function bundled with references to its surrounding state.',
        steps: [],
        durationMs: 3100,
      }),
    ],
  },
  {
    id: 'race-already-complete',
    title: 'Race: run already finished at reload',
    description:
      'The run completed between page load and the runs.get fetch. The snapshot ' +
      'reports `completed` with the full steps (incl. final answer); the resume ' +
      'branch renders a completed bubble immediately — no live stream.',
    runId: RUN_RACE,
    status: 'completed',
    seedSteps: [
      {
        type: 'reasoning',
        iteration: 0,
        timestamp: '2026-01-01T00:00:00Z',
        text: 'Quick lookup, then answer.',
      },
      {
        type: 'tool_call',
        iteration: 0,
        timestamp: '2026-01-01T00:00:01Z',
        toolName: 'web_search',
        toolDisplayName: 'Web Search',
        toolCallId: 'tc-race-search',
        input: { query: 'capital of France' },
        output: 'Paris',
        label: 'Searching the web',
      },
      {
        type: 'narration',
        iteration: 1,
        timestamp: '2026-01-01T00:00:02Z',
        text: 'The capital of France is Paris.',
      },
    ],
    liveEvents: [
      ev('agent.run.completed', RUN_RACE, {
        response: 'The capital of France is Paris.',
        steps: [],
        durationMs: 1900,
      }),
    ],
  },
];
